import isFunction from 'lodash/isFunction';
import { useCallback } from 'react';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import isBoolean from 'lodash/isBoolean';

import { FormPropsType } from 'src/components/UI/types';

type ReturnType = {
  hoverMessage: string | undefined;
  handleChangeInputHover: () => void;
};

type Props = {
  name: string;
  clearErrors: FormPropsType['clearErrors'];
  error: FieldErrors | false;
  showAnyError?: boolean;
  validationName?: string;
};

export const useHoverErrors = ({
  name,
  error,
  clearErrors,
  showAnyError = false,
  validationName,
}: Props): ReturnType => {
  /** return error in case of hover custom errors or any existing form errors */
  const isHoverError = !isBoolean(error) && error.type === 'hover';
  const isAnyError = !isBoolean(error) && showAnyError;
  const hoverMessage = isHoverError || isAnyError ? error.message : undefined;

  const handleChangeInputHover = useCallback(() => {
    if (isHoverError) {
      clearErrors(name);
      if (validationName) {
        clearErrors(validationName);
      }
    }
  }, [clearErrors, isHoverError, name, validationName]);

  return { hoverMessage, handleChangeInputHover };
};

type CleanProps = {
  name: string;

  clearErrors?: (name: string, type?: 'hover') => void;
  errors?: { type: string; message: string }[];
  showAnyError?: boolean;
  validateName?: string;
};

type CleanReturnType = { errorString: string | undefined; onChangeInputHover: (bruteClear?: boolean) => void };

export const useCleanHoverErrors = ({
  errors,
  name,
  clearErrors,
  showAnyError = false,
  validateName,
}: CleanProps): CleanReturnType => {
  const hoverErrorsExists = !!errors?.some(({ type }) => type === 'hover');

  // show all errors or hover errors only
  const localErrors = (showAnyError ? errors : errors?.filter(({ type }) => type === 'hover'))?.map(
    ({ message }) => message
  );

  const onChangeInputHover = useCallback(
    (bruteClear?: boolean) => {
      if (isFunction(clearErrors) && (hoverErrorsExists || bruteClear)) {
        clearErrors(name, 'hover');
        if (validateName) {
          clearErrors(validateName, 'hover');
        }
      }
    },
    [clearErrors, hoverErrorsExists, name, validateName]
  );

  return { errorString: localErrors?.join(',\n'), onChangeInputHover };
};
